import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ConnectionCreateBody,
  ConnectionUpdateBody,
  type ConnectionTestResult,
  type ConnectionView,
} from "@qlik-scheduler/shared";

import { ApiError } from "../api/client.js";
import {
  useConnections,
  useCreateConnection,
  useDeleteConnection,
  useTestConnectionSaved,
  useTestConnectionUnsaved,
  useUpdateConnection,
} from "../api/queries.js";
import { Banner } from "../components/Banner.js";
import { Button } from "../components/Button.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { ErrorAlert } from "../components/ErrorAlert.js";
import { Modal } from "../components/Modal.js";
import { Skeleton } from "../components/Skeleton.js";
import { StatusBadge, StatusDot } from "../components/StatusBadge.js";
import { useToast } from "../components/Toast.js";

export function ConnectionsPage() {
  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-8 sm:px-6 sm:py-10">
      <header className="mb-8">
        <p className="eyebrow">Connections</p>
        <h1 className="heading-underline mt-2 text-2xl font-extrabold uppercase tracking-[0.02em] text-ink">
          Connections
        </h1>
        <p className="mt-4 max-w-prose text-sm text-ink-soft">
          Manage Qlik Sense Cloud tenant connections. API keys are encrypted at
          rest.
        </p>
      </header>

      <section aria-labelledby="add-connection-heading" className="mb-10">
        <h2
          id="add-connection-heading"
          className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-soft"
        >
          Add new connection
        </h2>
        <AddConnectionForm />
      </section>

      <section aria-labelledby="connections-list-heading">
        <h2
          id="connections-list-heading"
          className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-soft"
        >
          Your connections
        </h2>
        <ConnectionList />
      </section>
    </div>
  );
}

// --- Add new connection form ------------------------------------------------

function AddConnectionForm() {
  const create = useCreateConnection();
  const testUnsaved = useTestConnectionUnsaved();
  const [showKey, setShowKey] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    getValues,
    trigger,
  } = useForm({
    resolver: zodResolver(ConnectionCreateBody),
    defaultValues: { connectionName: "", tenantUrl: "", apiKey: "" },
  });

  async function onTest() {
    const ok = await trigger(["tenantUrl", "apiKey"]);
    if (!ok) return;
    const { tenantUrl, apiKey } = getValues();
    try {
      const result = await testUnsaved.mutateAsync({ tenantUrl, apiKey });
      setTestResult(result);
    } catch (err) {
      // surface as a synthetic failed result so the inline banner renders.
      setTestResult({
        success: false,
        errorKind: "NETWORK",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }

  async function onSave(values: { connectionName: string; tenantUrl: string; apiKey: string }) {
    try {
      await create.mutateAsync(values);
      reset({ connectionName: "", tenantUrl: "", apiKey: "" });
      setTestResult(null);
      setShowKey(false);
    } catch {
      // error displayed inline below
    }
  }

  return (
    <form onSubmit={handleSubmit(onSave)} className="card p-6" noValidate>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FormField
          label="Connection name"
          htmlFor="cn-name"
          error={errors.connectionName?.message}
        >
          <input
            id="cn-name"
            type="text"
            className="input"
            placeholder="Production tenant"
            autoComplete="off"
            {...register("connectionName")}
          />
        </FormField>
        <FormField label="Tenant URL" htmlFor="cn-url" error={errors.tenantUrl?.message}>
          <input
            id="cn-url"
            type="url"
            className="input"
            placeholder="https://your-tenant.eu.qlikcloud.com"
            autoComplete="off"
            {...register("tenantUrl")}
          />
        </FormField>
        <FormField
          label="API key"
          htmlFor="cn-key"
          error={errors.apiKey?.message}
          className="md:col-span-2"
        >
          <div className="flex gap-2">
            <input
              id="cn-key"
              type={showKey ? "text" : "password"}
              className="input flex-1"
              autoComplete="off"
              placeholder="Paste your Qlik API key"
              {...register("apiKey")}
            />
            <Button
              variant="ghost-sm"
              onClick={() => setShowKey((v) => !v)}
              aria-pressed={showKey}
              aria-label={showKey ? "Hide API key" : "Show API key"}
            >
              {showKey ? "Hide" : "Show"}
            </Button>
          </div>
        </FormField>
      </div>

      {testResult ? (
        <div className="mt-5">
          <TestResultBanner result={testResult} onDismiss={() => setTestResult(null)} />
        </div>
      ) : null}

      {create.error ? (
        <div className="mt-5">
          <ErrorAlert>{describeError(create.error)}</ErrorAlert>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <Button
          variant="ghost"
          onClick={onTest}
          disabled={testUnsaved.isPending}
        >
          {testUnsaved.isPending ? "Testing…" : "Test Connection"}
        </Button>
        <Button type="submit" variant="primary" disabled={isSubmitting || create.isPending}>
          {create.isPending ? "Saving…" : "Save Connection"}
        </Button>
      </div>
    </form>
  );
}

function TestResultBanner({
  result,
  onDismiss,
}: {
  result: ConnectionTestResult;
  onDismiss: () => void;
}) {
  if (result.success) {
    const name = result.tenantInfo.name || result.tenantInfo.id;
    return (
      <Banner tone="success" title="Connection successful" onDismiss={onDismiss}>
        Tenant: {name}
      </Banner>
    );
  }
  return (
    <Banner
      tone="error"
      title={`Connection failed (${result.errorKind})`}
      onDismiss={onDismiss}
    >
      {result.message}
    </Banner>
  );
}

// --- Connection list --------------------------------------------------------

function ConnectionList() {
  const { data, isLoading, error } = useConnections();

  if (isLoading) {
    return (
      <ul className="space-y-3" aria-label="Loading connections" role="list">
        {Array.from({ length: 3 }, (_, i) => (
          <li key={i} className="card p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton width="40%" height={14} />
                <Skeleton width="65%" height={12} />
                <Skeleton width="30%" height={12} />
              </div>
              <div className="flex shrink-0 gap-2">
                <Skeleton width={70} height={32} rounded="full" />
                <Skeleton width={70} height={32} rounded="full" />
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  }
  if (error) {
    return <ErrorAlert>{describeError(error)}</ErrorAlert>;
  }
  const list = data ?? [];
  if (list.length === 0) {
    return (
      <div className="card empty-surface">
        <p className="text-base font-semibold text-ink">No connections yet</p>
        <p>Add your first Qlik tenant connection above to get started.</p>
      </div>
    );
  }
  return (
    <ul className="space-y-3" role="list">
      {list.map((conn) => (
        <ConnectionRow key={conn.id} connection={conn} />
      ))}
    </ul>
  );
}

function ConnectionRow({ connection }: { connection: ConnectionView }) {
  const toast = useToast();
  const test = useTestConnectionSaved();
  const del = useDeleteConnection();
  const [showError, setShowError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const tone: "green" | "red" | "grey" =
    connection.lastTestSuccess === true
      ? "green"
      : connection.lastTestSuccess === false
        ? "red"
        : "grey";

  const lastTested = connection.lastTestedAt
    ? `Last tested ${formatRelative(connection.lastTestedAt)}`
    : "Never tested";

  const chipLabel =
    tone === "green" ? "Healthy" : tone === "red" ? "Failed" : "Untested";

  async function onTest() {
    try {
      const result = await test.mutateAsync(connection.id);
      if (result.success) {
        toast.push("success", `Tested: ${result.tenantInfo.name || "OK"}`);
      } else {
        toast.push("error", `Test failed: ${result.message}`);
      }
    } catch (err) {
      toast.push("error", describeError(err));
    }
  }

  async function onDelete() {
    setDeleteError(null);
    try {
      await del.mutateAsync(connection.id);
      toast.push("success", "Connection deleted");
      setConfirmDelete(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detailIds = Array.isArray((err.details as { scheduleIds?: unknown })?.scheduleIds)
          ? ((err.details as { scheduleIds: unknown[] }).scheduleIds.join(", "))
          : null;
        setDeleteError(
          `${err.message}${detailIds ? ` (schedules: ${detailIds})` : ""}`,
        );
      } else {
        setDeleteError(describeError(err));
      }
    }
  }

  return (
    <li className="card card-hover p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <StatusDot tone={tone} title={statusDotTitle(connection)} />
            <h4 className="truncate text-base font-extrabold uppercase tracking-[0.02em] text-ink">
              {connection.connectionName}
            </h4>
            <StatusBadge tone={tone} title={statusDotTitle(connection)}>
              {chipLabel}
            </StatusBadge>
          </div>
          <p className="mt-1 truncate font-mono text-[12px] text-ink-soft">
            {connection.tenantUrl}
          </p>
          <p className="mt-0.5 text-[12px] text-ink-soft">
            API key: …{connection.apiKeyHint} · {lastTested}
          </p>
          {connection.lastTestSuccess === false && connection.lastTestError ? (
            <button
              type="button"
              onClick={() => setShowError((v) => !v)}
              className="mt-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-red-700 underline-offset-2 hover:underline"
              aria-expanded={showError}
            >
              {showError ? "Hide error" : "Show last error"}
            </button>
          ) : null}
          {showError && connection.lastTestError ? (
            <p className="mt-2 whitespace-pre-wrap rounded-card border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              {connection.lastTestError}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-2">
          <Button variant="ghost-sm" onClick={onTest} disabled={test.isPending}>
            {test.isPending ? "Testing…" : "Test"}
          </Button>
          <Button variant="ghost-sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <Button variant="danger-sm" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </div>
      </div>

      {editing ? (
        <EditConnectionModal connection={connection} onClose={() => setEditing(false)} />
      ) : null}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete connection"
        message={
          <div className="space-y-3">
            <p>
              Delete <strong>{connection.connectionName}</strong>? This cannot be undone.
            </p>
            {deleteError ? <ErrorAlert>{deleteError}</ErrorAlert> : null}
          </div>
        }
        confirmLabel="Delete"
        destructive
        busy={del.isPending}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteError(null);
        }}
        onConfirm={onDelete}
      />
    </li>
  );
}

function statusDotTitle(c: ConnectionView): string {
  if (c.lastTestSuccess === true) return "Last test succeeded";
  if (c.lastTestSuccess === false) return "Last test failed";
  return "Not yet tested";
}

// --- Edit connection modal --------------------------------------------------

function EditConnectionModal({
  connection,
  onClose,
}: {
  connection: ConnectionView;
  onClose: () => void;
}) {
  const update = useUpdateConnection(connection.id);
  const [showKey, setShowKey] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    // We allow apiKey to be empty (= keep current). ConnectionUpdateBody marks
    // apiKey as optional with min(20) when present — but zod min on optional
    // strings still trips on "". We pre-strip empty strings on submit instead.
    resolver: zodResolver(ConnectionUpdateBody),
    defaultValues: {
      connectionName: connection.connectionName,
      tenantUrl: connection.tenantUrl,
      apiKey: undefined as string | undefined,
    },
  });

  async function onSubmit(values: { connectionName?: string; tenantUrl?: string; apiKey?: string }) {
    const body: { connectionName?: string; tenantUrl?: string; apiKey?: string } = {
      connectionName: values.connectionName,
      tenantUrl: values.tenantUrl,
    };
    if (values.apiKey && values.apiKey.trim().length > 0) {
      body.apiKey = values.apiKey;
    }
    try {
      await update.mutateAsync(body);
      onClose();
    } catch {
      /* error inline */
    }
  }

  return (
    <Modal open onClose={onClose} title={`Edit ${connection.connectionName}`}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <FormField
          label="Connection name"
          htmlFor="edit-name"
          error={errors.connectionName?.message}
        >
          <input
            id="edit-name"
            type="text"
            className="input"
            {...register("connectionName")}
          />
        </FormField>
        <FormField label="Tenant URL" htmlFor="edit-url" error={errors.tenantUrl?.message}>
          <input
            id="edit-url"
            type="url"
            className="input"
            {...register("tenantUrl")}
          />
        </FormField>
        <FormField label="API key" htmlFor="edit-key" error={errors.apiKey?.message}>
          <div className="flex gap-2">
            <input
              id="edit-key"
              type={showKey ? "text" : "password"}
              className="input flex-1"
              autoComplete="off"
              placeholder="Enter new API key (leave blank to keep current)"
              {...register("apiKey")}
            />
            <Button
              variant="ghost-sm"
              onClick={() => setShowKey((v) => !v)}
              aria-pressed={showKey}
            >
              {showKey ? "Hide" : "Show"}
            </Button>
          </div>
        </FormField>
        {update.error ? <ErrorAlert>{describeError(update.error)}</ErrorAlert> : null}
        <div className="flex justify-end gap-3 border-t border-line pt-5">
          <Button variant="ghost-sm" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary-sm"
            disabled={isSubmitting || update.isPending}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// --- Shared helpers ---------------------------------------------------------

function FormField({
  label,
  htmlFor,
  error,
  children,
  className = "",
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="field-label mb-1.5 block">
        {label}
      </label>
      {children}
      {error ? (
        <p role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.code}: ${err.message}`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "Unknown error";
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "recently";
  const seconds = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
